import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, tokenStorage, getErrorMessage, sitesService } from '../services/api';

// Role constants - Backend'deki değerlerle birebir eşleşmeli
export const ROLES = {
  // Sistem Rolleri
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  
  // Hesap Yöneticisi
  SITE_MANAGER: 'site_manager',        // Legacy
  HESAP_YONETICISI: 'hesap_yoneticisi',
  
  // Yardımcı (Hesap Yöneticisi Yardımcısı)
  YARDIMCI: 'yardimci',
  
  // İçerik Yöneticisi (Haber Merkezi)
  ICERIK_YONETICISI: 'icerik_yoneticisi',
  
  // Hesap Kullanıcıları
  SITE_SAKINI: 'site_sakini',          // Apartman/site sakini (birincil)
  SAKINI: 'site_sakini',               // Alias - site_sakini ile aynı
  SOFOR: 'sofor',
  CALISAN: 'calisan',
  ZIYARETCI: 'ziyaretci',
  
  // Bireysel
  BIREYSEL_YONETICI: 'bireysel_yonetici',  // Legacy
  BIREYSEL_HESAP: 'bireysel_hesap',
  
  // Legacy
  USER: 'user'
};

// Role display names for UI
export const ROLE_LABELS = {
  super_admin: 'Süper Admin',
  admin: 'Admin',
  site_manager: 'Hesap Yöneticisi',
  hesap_yoneticisi: 'Hesap Yöneticisi',
  yardimci: 'Yardımcı',
  icerik_yoneticisi: 'İçerik Yöneticisi',
  site_sakini: 'Site Sakini',
  sakini: 'Site Sakini',
  sofor: 'Şoför',
  calisan: 'Çalışan',
  technician: 'Usta/Teknisyen',
  ziyaretci: 'Ziyaretçi',
  bireysel_yonetici: 'Bireysel Hesap',
  bireysel_hesap: 'Bireysel Hesap',
  user: 'Site Sakini'
};

// Account type labels
export const ACCOUNT_TYPE_LABELS = {
  apartman: 'Apartman/Site',
  fabrika: 'Fabrika',
  avm: 'AVM',
  lojistik: 'Lojistik/Filo',
  belediye: 'Belediye/Kamu',
  bireysel: 'Bireysel'
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ROL SİMÜLASYONU - Süper Admin için
  const [simulatedRole, setSimulatedRole] = useState(() => {
    // Sayfa yenilendiğinde simülasyon modunu sessionStorage'dan al
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('hims_simulated_role') || null;
    }
    return null;
  });
  
  // Simülasyon için site ID - Hesap Yöneticisi simülasyonunda kullanılır
  const [simulatedSiteId, setSimulatedSiteId] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('hims_simulated_site_id') || null;
    }
    return null;
  });

  // Simülasyon modunu başlat
  const startRoleSimulation = useCallback(async (role, siteId = null) => {
    if (user?.role === ROLES.SUPER_ADMIN) {
      setSimulatedRole(role);
      sessionStorage.setItem('hims_simulated_role', role);
      
      // Site ID doğrudan verilmişse kullan
      if (siteId) {
        setSimulatedSiteId(siteId);
        sessionStorage.setItem('hims_simulated_site_id', siteId);
        return;
      }
      
      // Hesap Yöneticisi veya Yardımcı simülasyonunda ilk sitenin ID'sini al
      if (role === 'hesap_yoneticisi' || role === 'site_manager' || role === 'yardimci') {
        try {
          const sitesData = await sitesService.list();
          const sites = Array.isArray(sitesData) ? sitesData : (sitesData?.sites || sitesData?.items || []);
          if (sites && sites.length > 0) {
            // "Ana Site" varsa onu seç, yoksa ilk siteyi
            const anaSite = sites.find(s => s.name === 'Ana Site');
            const selectedSite = anaSite || sites[0];
            setSimulatedSiteId(selectedSite.id);
            sessionStorage.setItem('hims_simulated_site_id', selectedSite.id);
          }
        } catch (err) {
          console.error('Failed to fetch sites for simulation:', err);
        }
      } else {
        // Diğer roller için site ID temizle
        setSimulatedSiteId(null);
        sessionStorage.removeItem('hims_simulated_site_id');
      }
    }
  }, [user]);

  // Simülasyon modunu bitir
  const stopRoleSimulation = useCallback(() => {
    setSimulatedRole(null);
    setSimulatedSiteId(null);
    sessionStorage.removeItem('hims_simulated_role');
    sessionStorage.removeItem('hims_simulated_site_id');
  }, []);

  // Simülasyon aktif mi?
  const isSimulationActive = useCallback(() => {
    return user?.role === ROLES.SUPER_ADMIN && simulatedRole !== null;
  }, [user, simulatedRole]);

  // Efektif rol - simülasyon aktifse simüle edilen rol, değilse gerçek rol
  const getEffectiveRole = useCallback(() => {
    if (user?.role === ROLES.SUPER_ADMIN && simulatedRole) {
      return simulatedRole;
    }
    return user?.role;
  }, [user, simulatedRole]);

  // Efektif site ID - simülasyon aktifse simüle edilen site, değilse gerçek site
  const getEffectiveSiteId = useCallback(() => {
    // Super Admin simülasyon modunda
    if (user?.role === ROLES.SUPER_ADMIN && simulatedRole) {
      // Hesap Yöneticisi veya Yardımcı simülasyonunda
      if (simulatedRole === 'hesap_yoneticisi' || simulatedRole === 'site_manager' || simulatedRole === 'yardimci') {
        // simulatedSiteId varsa kullan, yoksa null döndür (henüz yükleniyor)
        return simulatedSiteId || null;
      }
      // Diğer simülasyon rolleri için site ID yok
      return null;
    }
    // Simülasyon yok - gerçek kullanıcının site_id'si
    return user?.site_id || null;
  }, [user, simulatedRole, simulatedSiteId]);

  // Gerçek süper admin mi kontrolü (simülasyondan bağımsız)
  const isRealSuperAdmin = useCallback(() => {
    return user?.role === ROLES.SUPER_ADMIN;
  }, [user]);

  // Validate session on mount
  useEffect(() => {
    const validateSession = async () => {
      try {
        const storedUser = tokenStorage.getUser();
        const token = tokenStorage.get();
        
        if (!token) {
          setLoading(false);
          return;
        }

        // Try to validate with backend
        try {
          const validatedUser = await authService.me();
          setUser(validatedUser);
          tokenStorage.setUser(validatedUser);
        } catch (err) {
          // If validation fails, clear everything
          tokenStorage.clear();
          setUser(null);
        }
      } catch (err) {
        tokenStorage.clear();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    validateSession();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authService.login({ email, password });
      setUser(response.user);
      return response.user;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      // Throw an error with the message so LoginPage can use it
      const errorToThrow = new Error(message);
      errorToThrow.status = err.status;
      throw errorToThrow;
    }
  }, []);

  const register = useCallback(async (data) => {
    setError(null);
    try {
      const response = await authService.register(data);
      if (response.user) {
        setUser(response.user);
      }
      return response;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Clear anyway even if API fails
      // console.log('Logout API failed, clearing local state');
    }
    setUser(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Role checking utilities - Simülasyon desteği ile
  const hasRole = useCallback((role) => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === role;
  }, [getEffectiveRole]);

  const hasAnyRole = useCallback((roles) => {
    const effectiveRole = getEffectiveRole();
    return roles.includes(effectiveRole);
  }, [getEffectiveRole]);

  const isSuperAdmin = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.SUPER_ADMIN;
  }, [getEffectiveRole]);

  const isAdminRole = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.ADMIN;
  }, [getEffectiveRole]);

  const isSiteManager = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.SITE_MANAGER;
  }, [getEffectiveRole]);

  const isBireyselYonetici = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.BIREYSEL_YONETICI || effectiveRole === ROLES.BIREYSEL_HESAP;
  }, [getEffectiveRole]);

  const isHesapYoneticisi = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.HESAP_YONETICISI || effectiveRole === ROLES.SITE_MANAGER;
  }, [getEffectiveRole]);

  const isYardimci = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.YARDIMCI;
  }, [getEffectiveRole]);

  const isSiteSakini = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.SITE_SAKINI || effectiveRole === ROLES.USER || effectiveRole === ROLES.SAKINI;
  }, [getEffectiveRole]);

  const isCalisan = useCallback(() => {
    // Çalışan (Usta/Teknisyen)
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.CALISAN || effectiveRole === 'calisan';
  }, [getEffectiveRole]);

  const isAdmin = useCallback(() => {
    // Super Admin, Admin, Site Manager veya Hesap Yöneticisi
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.SUPER_ADMIN || 
           effectiveRole === ROLES.ADMIN || 
           effectiveRole === ROLES.SITE_MANAGER ||
           effectiveRole === ROLES.HESAP_YONETICISI;
  }, [getEffectiveRole]);

  const isSuperOrAdmin = useCallback(() => {
    // Super Admin veya Admin
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.SUPER_ADMIN || effectiveRole === ROLES.ADMIN;
  }, [getEffectiveRole]);

  const isUser = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.USER || effectiveRole === ROLES.SITE_SAKINI;
  }, [getEffectiveRole]);

  // Permission checks
  const canManageUsers = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canManageSites = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canManageStations = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canManageRFID = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canViewAllReports = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canManagePricing = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canAccessOCPP = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  const canAccessAuditLogs = useCallback(() => {
    // Super Admin ve Admin görebilir, Site Manager göremez
    return isSuperOrAdmin();
  }, [isSuperOrAdmin]);

  const canAccessBackups = useCallback(() => {
    // Sadece Super Admin
    return isSuperAdmin();
  }, [isSuperAdmin]);

  const canAccessScheduler = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin
  }, [isSuperAdmin]);

  const canAccessModuleManagement = useCallback(() => {
    // Super Admin ve Admin modül yönetimi yapabilir
    return isSuperOrAdmin();
  }, [isSuperOrAdmin]);

  const canAccessLicenses = useCallback(() => {
    return isAdmin();  // Site Manager sadece görüntüleyebilir
  }, [isAdmin]);

  const canAccessEnergy = useCallback(() => {
    return isAdmin();
  }, [isAdmin]);

  // Yeni yetki kontrolleri
  const canEditLicenses = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin düzenleyebilir
  }, [isSuperAdmin]);

  const canDeleteUsers = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin silebilir
  }, [isSuperAdmin]);

  const canDeleteStations = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin silebilir
  }, [isSuperAdmin]);

  const canAddAdminRoles = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin admin rolü ekleyebilir
  }, [isSuperAdmin]);

  const canCreateAdmin = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin admin oluşturabilir
  }, [isSuperAdmin]);

  const canAccessSettings = useCallback(() => {
    return isSuperAdmin();  // Sadece Super Admin sistem ayarlarına erişebilir
  }, [isSuperAdmin]);

  const canAccessOcppConfig = useCallback(() => {
    // OCPP teknik ayarları - sadece Super Admin
    return isSuperAdmin();
  }, [isSuperAdmin]);

  const canSetLicensePricing = useCallback(() => {
    // Lisans fiyat belirleme - Super Admin ve Admin
    return isSuperOrAdmin();
  }, [isSuperOrAdmin]);

  const canSeePricing = useCallback(() => {
    return isAdmin();  // User maliyet/fiyat göremez
  }, [isAdmin]);

  // İçerik Yöneticisi kontrolü
  const isIcerikYoneticisi = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.ICERIK_YONETICISI;
  }, [getEffectiveRole]);

  // İçerik yönetimi yetkisi (super_admin, admin, icerik_yoneticisi)
  const canManageContent = useCallback(() => {
    const effectiveRole = getEffectiveRole();
    return effectiveRole === ROLES.SUPER_ADMIN || 
           effectiveRole === ROLES.ADMIN || 
           effectiveRole === ROLES.ICERIK_YONETICISI;
  }, [getEffectiveRole]);

  // Kullanıcının rolüne göre varsayılan sayfa
  const getDefaultRoute = useCallback(() => {
    if (!user) return '/login';
    
    // Site Sakini -> Resident Dashboard
    if (user.role === ROLES.SITE_SAKINI || user.role === ROLES.USER || user.role === ROLES.SAKINI) {
      return '/resident/dashboard';
    }
    
    // Diğer tüm roller -> Normal Dashboard
    return '/dashboard';
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    clearError,
    getDefaultRoute,
    // Role Simulation (Süper Admin için)
    simulatedRole,
    simulatedSiteId,
    startRoleSimulation,
    stopRoleSimulation,
    isSimulationActive,
    getEffectiveRole,
    getEffectiveSiteId,
    isRealSuperAdmin,
    // Role checks
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isAdminRole,
    isSiteManager,
    isHesapYoneticisi,
    isBireyselYonetici,
    isSiteSakini,
    isYardimci,
    isAdmin,
    isSuperOrAdmin,
    isUser,
    // Permission checks
    canManageUsers,
    canManageSites,
    canManageStations,
    canManageRFID,
    canViewAllReports,
    canManagePricing,
    canAccessOCPP,
    canAccessAuditLogs,
    canAccessBackups,
    canAccessScheduler,
    canAccessModuleManagement,
    canAccessLicenses,
    canAccessEnergy,
    canEditLicenses,
    canDeleteUsers,
    canDeleteStations,
    canAddAdminRoles,
    canCreateAdmin,
    canAccessSettings,
    canAccessOcppConfig,
    canSetLicensePricing,
    canSeePricing,
    isCalisan,
    isIcerikYoneticisi,
    canManageContent,
    // Constants
    ROLES,
    ROLE_LABELS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
